/**
 * autoGrow — size a textarea to its content, up to a line limit, then let it scroll.
 *
 * A one-row field hides everything past the first line of a title somebody wrote in four; an
 * unbounded one pushes the rest of the panel off screen the moment a description gets long. So both
 * fields grow to fit and stop at a cap, and past it the field scrolls rather than the panel.
 *
 * Shared because the composer and Mission Control's prose fields (title, description) must size the same
 * way: a brief and a thread draft sit one click apart and read as one surface.
 */

/**
 * @param {HTMLTextAreaElement} node
 * @param {number} maxRows  lines to grow to before scrolling
 * @param {number} [maxHeight]  an absolute cap in px, which REPLACES the line-derived one. For a field
 *   whose own height is what was specified rather than a number of lines — the thread widget's, which
 *   has been 120px on a shipped surface since before this function existed.
 */
export function autoGrow(node, maxRows, maxHeight) {
  if (!node || !node.style) return;
  // Collapse first: `scrollHeight` reports the CONTENT height only when the box is not already
  // taller than it, so measuring without this makes a shrinking field stay at its high-water mark.
  node.style.height = 'auto';

  const style = typeof window !== 'undefined' && window.getComputedStyle
    ? window.getComputedStyle(node) : null;
  const number = (value, fallback) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const fontSize = style ? number(style.fontSize, 16) : 16;
  // `line-height: normal` does not parse, and is roughly 1.2 — but Bootstrap sets 1.5 on controls,
  // so the fallback matches what these fields actually render at.
  const lineHeight = style ? number(style.lineHeight, fontSize * 1.5) : fontSize * 1.5;
  // Controls are `border-box`, so the cap has to include what the content box does not.
  const chrome = style
    ? number(style.paddingTop, 0) + number(style.paddingBottom, 0)
      + number(style.borderTopWidth, 0) + number(style.borderBottomWidth, 0)
    : 0;

  const cap = Number.isFinite(maxHeight) ? maxHeight : lineHeight * maxRows + chrome;
  const content = node.scrollHeight;

  // A node with no layout (a modal body, mounted before the dialog is laid out) measures zero, and writing
  // that back leaves a sliver nothing re-measures. Leave `height` unset: `rows` is the resting size and
  // the next measurement lands with real numbers.
  if (!content) {
    node.style.height = '';
    node.style.overflowY = 'hidden';
    return;
  }

  const overflowing = content > cap;
  node.style.height = `${overflowing ? cap : content}px`;
  node.style.overflowY = overflowing ? 'auto' : 'hidden';
}

export default autoGrow;
